import * as Sentry from '@sentry/react'
import { clientesBack, adminUsuarioApi, organizationApi, registroUsuarioApi } from '@/common/api/https'

/**
 * Obtiene la lista de cargos disponibles
 * @returns {Promise<{code: number, data: Array, headers?: object}>}
 */
export const getCargos = async () => {
  try {
    const res = await organizationApi.get('/parametros/cargo')
    return {
      code: res.status,
      data: res.data?.data || res.data || [],
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'ORGANIZATION')
        scope.setTag('endpoint', '/parametros/cargo')
        scope.setExtras({ method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: [],
      message: e?.response?.data?.message || 'Error desconocido',
    }
  }
}

/**
 * Obtiene la lista de Áreas de afinidad disponibles
 * @returns {Promise<{code: number, data: Array, headers?: object}>}
 */
export const getAreasAfinidad = async () => {
  try {
    const res = await organizationApi.get('/parametros/area')
    return {
      code: res.status,
      data: res.data?.data || res.data || [],
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'ORGANIZATION')
        scope.setTag('endpoint', '/parametros/area')
        scope.setExtras({ method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: [],
      message: e?.response?.data?.message || 'Error desconocido',
    }
  }
}

/**
 * Obtiene los contratos y solicitudes pendientes del usuario
 * @param {number} idUsuario - ID del usuario
 * @returns {Promise<{code: number, data: Array, headers?: object}>}
 */
export const getContratosUsuario = async (idUsuario) => {
  try {
    const res = await adminUsuarioApi.get('/usuarios/contratos', {
      params: { idUsuario },
    })
    return {
      code: res.status,
      data: res.data || [],
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'ADMIN_USUARIO')
        scope.setTag('endpoint', '/usuarios/contratos')
        scope.setExtras({ idUsuario, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: [],
      message: e?.response?.data?.message || 'Error al obtener contratos',
    }
  }
}

/**
 * Obtiene los datos personales del usuario
 * @param {number} idUsuario - ID del usuario
 * @returns {Promise<{code: number, data: object|null, headers?: object}>}
 */
export const getDatosPersonales = async (idUsuario) => {
  try {
    const res = await clientesBack.get('/v2/usuarios/datos-personales', {
      params: { idUsuario },
    })
    return {
      code: res.status,
      data: res.data || null,
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v2/usuarios/datos-personales')
        scope.setExtras({ idUsuario, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al obtener datos personales',
    }
  }
}

/**
 * Obtiene los atributos administrativos del usuario
 * @param {number} idUsuario - ID del usuario
 * @param {number} idSistemaWeb - ID del sistema web (default: 1)
 * @param {number} [contrato] - Contrato seleccionado (opcional)
 * @returns {Promise<{code: number, data: Array, headers?: object}>}
 */
export const getAtributosUsuario = async (idUsuario, idSistemaWeb = 1, contrato = null) => {
  try {
    const params = { idUsuario, idSistemaWeb }
    if (contrato) {
      params.contrato = contrato
    }
    const res = await clientesBack.get('/v2/usuarios/atributos', {
      params,
    })
    return {
      code: res.status,
      data: res.data || [],
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v2/usuarios/atributos')
        scope.setExtras({ idUsuario, idSistemaWeb, contrato, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: [],
      message: e?.response?.data?.message || 'Error al obtener atributos del usuario',
    }
  }
}

/**
 * Actualiza los datos personales del usuario
 * @param {Object} datosPersonales - Datos personales a actualizar
 * @param {number} datosPersonales.idUsuario - ID del usuario
 * @param {string} datosPersonales.apellido - Apellido del usuario
 * @param {string} datosPersonales.nombre - Nombre del usuario
 * @param {number} datosPersonales.tipoTelefono - Tipo de teléfono principal (ID)
 * @param {string} datosPersonales.telefono - Número de teléfono principal
 * @param {number} [datosPersonales.tipoTelefonoAlt] - Tipo de teléfono alternativo (ID)
 * @param {string} [datosPersonales.telefonoAlt] - Número de teléfono alternativo
 * @returns {Promise<{code: number, data: object|null, message?: string}>}
 */
export const updateDatosPersonales = async (datosPersonales) => {
  try {
    const res = await clientesBack.post('/v2/usuarios/datos-personales', datosPersonales)
    return {
      code: res.status,
      data: res.data || null,
      message: 'Datos personales actualizados correctamente',
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v2/usuarios/datos-personales')
        scope.setExtras({ datosPersonales, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al actualizar datos personales',
    }
  }
}

/**
 * Registra una solicitud de asociación de nueva cuenta para el usuario
 * @param {Object} payload - Datos de la solicitud
 * @param {string} payload.email - Email del usuario
 * @param {string} payload.cuit - CUIT sin guiones
 * @param {string} payload.apellido - Apellido del usuario
 * @param {string} payload.nombre - Nombre del usuario
 * @param {string} payload.idCargo - ID del cargo seleccionado
 * @param {string} payload.idAreaAfinidad - ID del área de afinidad seleccionada
 * @param {string|number} payload.tipoTelefono - ID del tipo de teléfono
 * @param {string} payload.numeroTelefono - Número de teléfono
 * @param {string} payload.aceptoTerminos - 'S' o 'N'
 * @param {string} payload.usuarioAlta - Email del usuario que da de alta
 * @returns {Promise<{code: number, data: object|null, message?: string}>}
 */
export const asociarCuenta = async (payload) => {
  try {
    const res = await registroUsuarioApi.post('/solicitudes', payload, {
      params: { validaMail: false },
    })
    return {
      code: res.status,
      data: res.data || null,
      message: 'Solicitud de asociación enviada correctamente',
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'REGISTRO_USUARIO')
        scope.setTag('endpoint', '/solicitudes')
        scope.setExtras({ method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al enviar la solicitud de asociación',
    }
  }
}
